/**
 * Catalog list — `/catalog/products` (SPEC screen 1). Server-driven cursor
 * DataTable with sku/status/category filters. The SKU filter is EXACT citext
 * equality on the server (never trimmed, normalized, or fuzzy): nothing is
 * normalised client-side, values display exactly as stored, and a hit on a
 * child variant surfaces via the row's `matched_variant` hint.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Button,
  Combobox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { DataTable, badgeCell, dateTimeCell, textCell } from "@/components/data-table";
import type { SortState } from "@/components/data-table";
import { localizedName } from "@/lib/i18n";

import { PRODUCT_STATUSES, type Product, type ProductStatus, type Variant } from "../api";
import {
  useCategories,
  useProductList,
  useVariantOptions,
  type ProductListFilters,
} from "../queries";
import { BarcodeScanBox } from "../components/barcode-scan-box";

const ALL = "__all__";

export interface CatalogListPageProps {
  /** Row / scan-box hit → product editor (optionally deep-linked to a variant). */
  onOpenProduct: (productId: string, variantId?: string) => void;
  onCreateProduct: () => void;
  onOpenImports: () => void;
  /** Start the permission-guarded zero-hit registration handoff. */
  onRegisterBarcode?: (barcode: string) => void;
  /** Leave the target-selection handoff without changing data. */
  onCancelBarcodeRegistration?: () => void;
  /** Explicit target selected → open the ETag-backed editor's barcode tab. */
  onOpenBarcodeRegistration?: (productId: string, variantId: string, barcode: string) => void;
  /** `?sku=` deep link (cycle-loop links, duplicate-owner links). */
  initialSku?: string;
  /** Permission-guarded `?register_barcode=` value from the production route. */
  registrationBarcode?: string;
  /** Page size override (tests exercise cursor paging with a small limit). */
  pageLimit?: number;
  /** Production routes use the global shell slot; isolated/floor renders stay inline. */
  scanPlacement?: "inline" | "topbar";
  canScan?: boolean;
  canRegisterBarcode?: boolean;
  canCreateProduct?: boolean;
  canOpenImports?: boolean;
  canReadCategories?: boolean;
}

export function CatalogListPage({
  onOpenProduct,
  onCreateProduct,
  onOpenImports,
  onRegisterBarcode,
  onCancelBarcodeRegistration,
  onOpenBarcodeRegistration,
  initialSku,
  registrationBarcode,
  pageLimit,
  scanPlacement = "inline",
  canScan = true,
  canRegisterBarcode = false,
  canCreateProduct = true,
  canOpenImports = true,
  canReadCategories = true,
}: CatalogListPageProps) {
  const { t, i18n } = useTranslation();
  const [sku, setSku] = useState(initialSku ?? "");
  const [status, setStatus] = useState<ProductStatus | typeof ALL>(ALL);
  const [categoryId, setCategoryId] = useState<string>(ALL);
  const [sort, setSort] = useState<SortState>(null);
  const [registrationVariantId, setRegistrationVariantId] = useState<string | null>(null);

  useEffect(() => {
    setRegistrationVariantId(null);
  }, [registrationBarcode]);

  const filters = useMemo<ProductListFilters>(() => {
    const built: ProductListFilters = {};
    if (sku !== "") built.sku = sku;
    if (status !== ALL) built.status = status;
    if (categoryId !== ALL) built.category_id = categoryId;
    return built;
  }, [sku, status, categoryId]);

  const hasFilters = Object.keys(filters).length > 0;
  const query = useProductList(filters, sort, pageLimit);

  const categoriesQuery = useCategories(canReadCategories);
  const categories = categoriesQuery.data ?? [];
  const categoryName = (id: string): string => {
    const category = categories.find((candidate) => candidate.id === id);
    return category === undefined
      ? id
      : localizedName(category.name, category.name_ar, i18n.language);
  };

  // The picker uses the generated, abortable, fully bounded variant transport.
  // No mutation happens here: the user first confirms an exact target, then
  // the ETag-backed product editor performs the idempotent barcode POST.
  const registrationVariantsQuery = useVariantOptions(
    "",
    registrationBarcode !== undefined && canRegisterBarcode,
  );
  const registrationVariants = useMemo(
    () => registrationVariantsQuery.data ?? [],
    [registrationVariantsQuery.data],
  );
  const registrationOptions = useMemo(
    () =>
      registrationVariants.map((variant) => ({
        value: variant.id,
        label: variant.sku,
      })),
    [registrationVariants],
  );
  const registrationVariant =
    registrationVariants.find((variant) => variant.id === registrationVariantId) ?? null;

  const typeLabel = (value: string): string => t(`catalog.list.type.${value}`, value);
  const statusLabel = (value: string): string => t(`catalog.list.status.${value}`, value);

  const columns = useMemo<ColumnDef<Product>[]>(
    () => [
      {
        accessorKey: "name",
        id: "name",
        header: t("catalog.list.columns.name", "Name"),
        cell: (ctx) => {
          const row = ctx.row.original;
          return (
            <span className="flex flex-col">
              <span className="font-medium">
                {localizedName(row.name, row.name_ar, i18n.language)}
              </span>
              {row.matched_variant !== undefined ? (
                <span className="text-xs text-muted-foreground">
                  {t("catalog.list.matchedVariant", "Matched variant")}{" "}
                  <span dir="ltr" className="font-mono">
                    {row.matched_variant.sku}
                  </span>
                </span>
              ) : null}
            </span>
          );
        },
      },
      {
        accessorKey: "product_type",
        id: "product_type",
        header: t("catalog.list.columns.type", "Type"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<string>();
          return badgeCell(
            value,
            { goods: "secondary", service: "outline", bundle: "default" },
            typeLabel(value),
          );
        },
      },
      {
        accessorKey: "status",
        id: "status",
        header: t("catalog.list.columns.status", "Status"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<string>();
          return badgeCell(
            value,
            { draft: "outline", active: "success", archived: "secondary" },
            statusLabel(value),
          );
        },
      },
      {
        accessorKey: "category_id",
        id: "category",
        header: t("catalog.list.columns.category", "Category"),
        enableSorting: false,
        cell: (ctx) => textCell(categoryName(ctx.getValue<string>() ?? "")),
      },
      {
        accessorKey: "variant_count",
        id: "variant_count",
        header: t("catalog.list.columns.variants", "Variants"),
        enableSorting: false,
        meta: { numeric: true },
        // variant_count is a derived int — safe in a JS number (not money/qty).
        cell: (ctx) => String(ctx.getValue<number>()),
      },
      {
        accessorKey: "updated_at",
        id: "updated_at",
        header: t("catalog.list.columns.updated", "Updated"),
        cell: (ctx) => dateTimeCell(ctx.getValue<string>()),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- label helpers derive from t/categories
    [t, categories],
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">{t("catalog.list.title", "Catalog")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("catalog.list.subtitle", "Products, variants and barcodes.")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canScan ? (
            <BarcodeScanBox
              placement={scanPlacement}
              onHit={(variant: Variant) => onOpenProduct(variant.product_id, variant.id)}
              canRegisterBarcode={canRegisterBarcode}
              onRegister={onRegisterBarcode}
            />
          ) : null}
          {canOpenImports ? (
            <Button variant="outline" onClick={onOpenImports}>
              {t("catalog.list.importCsv", "Import CSV")}
            </Button>
          ) : null}
          {canCreateProduct ? (
            <Button onClick={onCreateProduct}>{t("catalog.list.newProduct", "New product")}</Button>
          ) : null}
        </div>
      </div>

      {registrationBarcode !== undefined && canRegisterBarcode ? (
        <section
          aria-labelledby="catalog-register-barcode-title"
          className="flex flex-col gap-3 rounded-md border border-primary/30 bg-muted/30 p-4"
        >
          <div className="space-y-1">
            <h2 id="catalog-register-barcode-title" className="text-sm font-semibold">
              {t("catalog.scan.registrationTitle", "Choose the variant for this barcode")}
            </h2>
            <p className="text-xs text-muted-foreground">
              {t(
                "catalog.scan.registrationHint",
                "The product editor will open with the barcode prefilled. Review the target before assigning it.",
              )}
            </p>
            <code dir="ltr" className="block w-fit rounded border bg-background px-2 py-1 text-xs">
              {registrationBarcode}
            </code>
          </div>
          {registrationVariantsQuery.isError ? (
            <div
              role="alert"
              className="flex flex-wrap items-center gap-2 text-sm text-destructive"
            >
              <span>
                {t("catalog.scan.registrationLoadError", "Variants could not be loaded.")}
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => void registrationVariantsQuery.refetch()}
              >
                {t("actions.retry")}
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex min-w-64 flex-col gap-1.5">
                <Label htmlFor="catalog-register-barcode-variant">
                  {t("catalog.scan.registrationVariant", "Target variant")}
                </Label>
                <Combobox
                  id="catalog-register-barcode-variant"
                  options={registrationOptions}
                  value={registrationVariantId}
                  onChange={setRegistrationVariantId}
                  disabled={registrationVariantsQuery.isLoading}
                  placeholder={t("catalog.scan.registrationPick", "Choose a variant…")}
                  searchPlaceholder={t("catalog.scan.registrationSearch", "Search SKU…")}
                  emptyMessage={t("catalog.scan.registrationEmpty", "No variants found.")}
                  className="w-72 max-w-full"
                />
              </div>
              <Button
                type="button"
                disabled={registrationVariant === null || onOpenBarcodeRegistration === undefined}
                onClick={() => {
                  if (registrationVariant === null || onOpenBarcodeRegistration === undefined)
                    return;
                  onOpenBarcodeRegistration(
                    registrationVariant.product_id,
                    registrationVariant.id,
                    registrationBarcode,
                  );
                }}
              >
                {t("catalog.scan.registrationContinue", "Continue to barcode editor")}
              </Button>
              {onCancelBarcodeRegistration !== undefined ? (
                <Button type="button" variant="ghost" onClick={onCancelBarcodeRegistration}>
                  {t("actions.cancel")}
                </Button>
              ) : null}
            </div>
          )}
        </section>
      ) : null}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="catalog-filter-sku">{t("catalog.list.filters.sku", "SKU")}</Label>
          <Input
            id="catalog-filter-sku"
            // SKUs are Latin codes — LTR even inside the RTL layout.
            dir="ltr"
            value={sku}
            onChange={(event) => setSku(event.target.value)}
            placeholder={t("catalog.list.filters.skuPlaceholder", "Exact SKU…")}
          />
          <p className="text-xs text-muted-foreground">
            {t(
              "catalog.list.filters.skuHint",
              "Exact match — case-insensitive, spelling significant.",
            )}
          </p>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="catalog-filter-status">
            {t("catalog.list.filters.status", "Status")}
          </Label>
          <Select
            value={status}
            onValueChange={(value) => setStatus(value as ProductStatus | typeof ALL)}
          >
            <SelectTrigger id="catalog-filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("catalog.list.filters.all", "All")}</SelectItem>
              {PRODUCT_STATUSES.map((value) => (
                <SelectItem key={value} value={value}>
                  {statusLabel(value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {canReadCategories ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="catalog-filter-category">
              {t("catalog.list.filters.category", "Category")}
            </Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger id="catalog-filter-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t("catalog.list.filters.all", "All")}</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {localizedName(category.name, category.name_ar, i18n.language)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
      </div>

      {hasFilters ? (
        <div>
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            onClick={() => {
              setSku("");
              setStatus(ALL);
              setCategoryId(ALL);
            }}
          >
            {t("catalog.list.clearFilters", "Clear filters")}
          </Button>
        </div>
      ) : null}

      <DataTable
        columns={columns}
        query={query}
        getRowId={(row) => row.id}
        sort={sort}
        onSortChange={setSort}
        onRowClick={(row) => onOpenProduct(row.id)}
        storageKey="catalog.products.list.columns"
        emptyMessage={
          hasFilters
            ? t("catalog.list.emptyFiltered", "No products match. Clear a filter and try again.")
            : t("catalog.list.empty", "No products yet — create one or import a CSV.")
        }
        errorMessage={t("catalog.list.loadError", "Products could not be loaded.")}
      />
    </div>
  );
}
